import { useState, useCallback } from 'react';

export interface ConfirmationOptions {
  type?: 'info' | 'warning' | 'danger';
  title: string;
  message: string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  showIcon?: boolean;
}

export const useConfirmationDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<ConfirmationOptions | null>(null);
  const [primaryAction, setPrimaryAction] = useState<(() => void) | null>(null);
  const [secondaryAction, setSecondaryAction] = useState<(() => void) | null>(null);

  const showConfirmation = useCallback((
    options: ConfirmationOptions,
    onConfirm: () => void,
    onCancel?: () => void
  ) => {
    setConfig({
      type: 'info',
      primaryButtonText: 'Confirm',
      secondaryButtonText: 'Cancel',
      showIcon: true,
      ...options
    });
    setPrimaryAction(() => onConfirm);
    setSecondaryAction(() => onCancel || (() => {}));
    setIsOpen(true);
  }, []);

  const hideConfirmation = useCallback(() => {
    setIsOpen(false);
    setConfig(null);
    setPrimaryAction(null);
    setSecondaryAction(null);
  }, []);

  // Predefined confirmation types
  const showEmailVerification = useCallback((onConfirm: () => void) => {
    showConfirmation({
      type: 'info',
      title: 'Verify your mail address.',
      message: 'Verification link has already sent to your email',
      primaryButtonText: 'Got it',
      secondaryButtonText: undefined,
      showIcon: false
    }, onConfirm);
  }, [showConfirmation]);

  const showDeleteAccount = useCallback((onConfirm: () => void, onCancel?: () => void) => {
    showConfirmation({
      type: 'danger',
      title: 'Are you sure you want to delete your account?',
      message: 'This action is permanent and cannot be undone.',
      primaryButtonText: 'Yes, Delete',
      secondaryButtonText: 'Cancel'
    }, onConfirm, onCancel);
  }, [showConfirmation]);

  const showCancelSubscription = useCallback((onConfirm: () => void, onCancel?: () => void) => {
    showConfirmation({
      type: 'warning',
      title: 'Are you sure you would like to cancel subscription?',
      message: "You'll lose access to all premium features at the end of your billing period.",
      primaryButtonText: 'Yes, Cancel It',
      secondaryButtonText: 'Keep Subscription'
    }, onConfirm, onCancel);
  }, [showConfirmation]);

  return {
    isOpen,
    config,
    primaryAction,
    secondaryAction,
    showConfirmation,
    hideConfirmation,
    showEmailVerification,
    showDeleteAccount,
    showCancelSubscription
  };
}; 